import React, { RefObject, useEffect, useRef } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useForm } from 'react-hook-form';

import classnames from 'classnames/bind';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import BaseModal from '@MODALS/BaseModal';
import HiddenFormButton from '@COMPONENTS/COMMON/buttons/HiddenFormButton';
import BasicInputValidatable from '@COMPONENTS/COMMON/inputs/input/BasicInput/BasicInputValidatable.component';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';

import { notify } from '@NOTIFICATION/Notificator';

import styles from './NameInputModal.module.scss';

const cx: CX = classnames.bind(styles);

enum Form {
    NAME = 'name',
}

yup.setLocale({
    mixed: {
        required: () => 'Name field is required',
    },
});

const schema = yup.object({
    [Form.NAME]: yup.string().required(),
});

export type FormValues = {
    [Form.NAME]: string;
};

function NameInputModal(props: Props) {
    const {
        modalTitle,
        itemName,
        initialInputValue,
        inputPlaceholder,
        successMessage,
        actionType = 'create',
        isLoading,
        isSuccess,
        isError,
        error,
        onSave,
        onClose,
    } = props;

    const { handleSubmit, control } = useForm<FormValues>({
        defaultValues: {
            [Form.NAME]: initialInputValue,
        },
        resolver: yupResolver(schema),
        mode: 'onChange',
    });

    const formButtonRef: RefObject<HTMLButtonElement> = useRef(null);

    useEffect(() => {
        if (isSuccess) {
            notify.info(successMessage, {
                toastId: `add-new-${itemName.toLowerCase()}`,
            });

            onClose();
        }
    }, [itemName, successMessage, isSuccess, onClose]);

    useEffect(() => {
        if (isError && error) {
            if ((error as FetchBaseQueryError).status === 400) {
                notify.error(`${itemName} with this name already exists`, {
                    toastId: `add-new-${itemName.toLowerCase()}-error`,
                });
            } else if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: `add-new-${itemName.toLowerCase()}-permission-error`,
                });
            } else {
                notify.error('Something wrong!', {
                    toastId: 'something-wrong',
                });
            }
        }
    }, [itemName, isError, error]);

    return (
        <BaseModal onClose={onClose}>
            <div className={cx('name-input-modal')}>
                <div className={cx('modal-header')}>
                    <div className={cx('icon-wrapper')}>
                        {actionType === 'create' ? (
                            <svg 
                                className={cx('modal-icon')} 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path 
                                    d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" 
                                    fill="currentColor"
                                />
                            </svg>
                        ) : (
                            <svg 
                                className={cx('modal-icon')} 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path 
                                    d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V18C2 18.5304 2.21071 19.0391 2.58579 19.4142C2.96086 19.7893 3.46957 20 4 20H16C16.5304 20 17.0391 19.7893 17.4142 19.4142C17.7893 19.0391 18 18.5304 18 18V11" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                />
                                <path 
                                    d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                    </div>
                    <div className={cx('header-content')}>
                        <h2 className={cx('title')}>{modalTitle}</h2>
                        <p className={cx('subtitle')}>
                            Enter a name for your new
                            {' '}
                            {itemName.toLowerCase()}
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSave)} className={cx('form')}>
                    <div className={cx('input-section')}>
                        <BasicInputValidatable<FormValues>
                            label={`${itemName} Name`}
                            placeholder={inputPlaceholder}
                            validation={{
                                name: Form.NAME,
                                control,
                            }}
                        />
                    </div>
                    <HiddenFormButton ref={formButtonRef} />
                </form>

                <div className={cx('modal-actions')}>
                    <BasicButton
                        variant="neutral"
                        size="medium"
                        title="Cancel"
                        onClick={onClose}
                        style={{ flex: 1 }}
                    />
                    <BasicButton
                        variant={actionType === 'create' ? 'success' : 'primary'}
                        size="medium"
                        title={
                            isLoading 
                                ? `${actionType === 'create' ? 'Creating' : 'Updating'}...`
                                : `${actionType === 'create' ? 'Create' : 'Update'} ${itemName}`
                        }
                        isProcessing={isLoading}
                        onClick={() => {
                            formButtonRef.current?.click();
                        }}
                        style={{ flex: 2 }}
                    />
                </div>
            </div>
        </BaseModal>
    );
}

NameInputModal.defaultProps = {
    initialInputValue: '',
    inputPlaceholder: '',
    actionType: 'create',
};

NameInputModal.propTypes = {
    modalTitle: PropTypes.string.isRequired,
    itemName: PropTypes.string.isRequired,
    initialInputValue: PropTypes.string,
    inputPlaceholder: PropTypes.string,
    successMessage: PropTypes.string.isRequired,
    actionType: PropTypes.oneOf(['create', 'update'] as const),
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isSuccess: PropTypes.bool.isRequired,
    isError: PropTypes.bool.isRequired,
};

type Props = InferProps<typeof NameInputModal.propTypes> & typeof NameInputModal.defaultProps & {
    error: FetchBaseQueryError | SerializedError | undefined,
};

export default NameInputModal;
